import type { WorksheetFormElementEntity, WorksheetFormEntity } from '../../../worksheets/forms/entities';

import { WORKSHEET_ELEMENT_TYPE_GROUPS } from './constants';
import { getGridCells } from './transformers';

/**
 * Check whether a single worksheet element has a valid answer, considering
 * its type and whether it is required. Read-only and non-required elements
 * always pass.
 *
 * @param element - the worksheet element to validate
 * @returns true if the element's answer is valid
 * @example
 * ```ts
 * checkElementAnswer(requiredTextElement); // false if empty
 * checkElementAnswer(optionalElement);     // true always
 * ```
 */
export function checkElementAnswer(element: WorksheetFormElementEntity): boolean {
    if (!element.isRequired || isReadOnlyElementType(element)) {
        return true;
    }

    if (isFileElementType(element)) {
        return hasFileAnswer(element);
    }

    if (isGridElementType(element)) {
        return hasValidGridAnswers(element);
    }

    return hasSelectedOptionAnswer(element);
}

/**
 * Collect element IDs that are conditional targets (shown/hidden based on
 * a parent element's selected option). These IDs are excluded from
 * unconditional validation.
 *
 * @param elements - all worksheet elements to scan for conditional relationships
 * @returns an array of element IDs that are conditional targets
 * @example
 * ```ts
 * const conditionalIds = collectConditionalElementIds(worksheetElements);
 * ```
 */
export function collectConditionalElementIds(elements: WorksheetFormElementEntity[]): string[] {
    const { conditionalOptionFormElementTypes } = WORKSHEET_ELEMENT_TYPE_GROUPS;

    return elements.reduce((ids: string[], element) => {
        if (conditionalOptionFormElementTypes.includes(element.worksheetElementTypeId)) {
            element.options.forEach((opt) => {
                const trgIdList = opt.trgtWorksheetElementIdList;
                if (trgIdList?.length) {
                    ids = [...ids, ...trgIdList];
                }
            });
        }
        return ids;
    }, []);
}

/**
 * Count the number of required fields that do not have valid answers,
 * excluding conditional target elements. Used to display the invalid
 * field count badge in the worksheet editor.
 *
 * @param worksheetData - the worksheet form entity containing elements
 * @returns the number of invalid required fields
 * @example
 * ```ts
 * const count = countInvalidRequiredFields(worksheetData); // 3
 * ```
 */
export function countInvalidRequiredFields(worksheetData: WorksheetFormEntity): number {
    const conditionalTargetIds = collectConditionalElementIds(worksheetData.elements);

    return worksheetData.elements.reduce((count, element) => {
        if (!conditionalTargetIds.includes(element.worksheetElementId)) {
            if (!checkElementAnswer(element)) {
                return count + 1;
            }
        }
        return count;
    }, 0);
}

/**
 * Check whether a file element has at least one uploaded answer asset.
 *
 * @param element - the worksheet element to check
 * @returns true if the element has one or more answer assets
 * @example
 * ```ts
 * hasFileAnswer(elementWithAssets); // true
 * ```
 */
export function hasFileAnswer(element: WorksheetFormElementEntity): boolean {
    return Boolean(element.answerAssets?.length);
}

/**
 * Check whether an input element has a non-empty text answer.
 *
 * @param element - the worksheet element to check
 * @returns true if the first selected option has non-empty answerText
 * @example
 * ```ts
 * hasInputAnswer(elementWithText); // true
 * ```
 */
export function hasInputAnswer(element: WorksheetFormElementEntity): boolean {
    const answerText = element.selectedOptions?.[0]?.answerText;
    if (answerText !== undefined && answerText !== '') {
        return true;
    }
    return false;
}

/**
 * Check whether a rating-with-text-area element has a valid rating answer.
 * A valid answer means a rating option (not the text-area option) is selected.
 *
 * @param element - the worksheet element to check
 * @returns true if a rating option is selected
 * @example
 * ```ts
 * hasRatingAnswer(ratingElementWithSelection); // true
 * ```
 */
export function hasRatingAnswer(element: WorksheetFormElementEntity): boolean {
    const textAreaOption = element.options[element.options.length - 1];
    const selectedOptions = element.selectedOptions ?? [];

    if (selectedOptions.length === 1) {
        return selectedOptions[0].optionId !== textAreaOption?.optionId;
    }

    const selectedRatingOption = selectedOptions.find((opt) => opt.optionId !== textAreaOption?.optionId);
    return selectedRatingOption?.optionLabel !== '';
}

/**
 * Check whether an element with selected options has a valid answer.
 * Dispatches to the appropriate check based on element type (input, rating, or generic).
 *
 * @param element - the worksheet element to check
 * @returns true if the element has a valid selected option answer
 * @example
 * ```ts
 * hasSelectedOptionAnswer(radioElement); // true if an option is selected
 * ```
 */
export function hasSelectedOptionAnswer(element: WorksheetFormElementEntity): boolean {
    if (!element.selectedOptions?.length) {
        return false;
    }

    if (isInputElementType(element)) {
        return hasInputAnswer(element);
    }

    if (isRatingWithTextAreaElementType(element)) {
        return hasRatingAnswer(element);
    }

    return element.selectedOptions[0].optionLabel !== '';
}

/**
 * Check whether a grid element has valid answers for all required cells.
 * If no cells are required, at least one cell must have a non-empty value.
 *
 * @param element - the worksheet grid element to validate
 * @returns true if grid validation passes
 * @example
 * ```ts
 * hasValidGridAnswers(gridElementWithAnswers); // true
 * ```
 */
export function hasValidGridAnswers(element: WorksheetFormElementEntity): boolean {
    const requiredCellIds = getGridCells(element)
        .filter((cell) => cell.isRequired)
        .map((cell) => cell.worksheetElementGridId);
    const answers = element.worksheetGridCellAnswers ?? [];

    if (requiredCellIds.length === 0) {
        return answers.some((answer) => answer?.value?.trim() !== '');
    }

    if (answers.length < requiredCellIds.length) {
        return false;
    }

    return requiredCellIds.every((id) =>
        answers.some((answer) => answer.worksheetElementGridId === id && answer?.value?.trim() !== '')
    );
}

/**
 * Check whether an element type is a file upload element.
 *
 * @param element - the worksheet element to check
 * @returns true if the element accepts file uploads
 * @example
 * ```ts
 * isFileElementType(fileElement); // true
 * ```
 */
export function isFileElementType(element: WorksheetFormElementEntity): boolean {
    return WORKSHEET_ELEMENT_TYPE_GROUPS.fileFormElementTypes.includes(element.worksheetElementTypeId);
}

/**
 * Check whether an element type is a grid element (dynamic, pressure test, etc.).
 *
 * @param element - the worksheet element to check
 * @returns true if the element uses a grid layout
 * @example
 * ```ts
 * isGridElementType(dynamicGridElement); // true
 * ```
 */
export function isGridElementType(element: WorksheetFormElementEntity): boolean {
    return WORKSHEET_ELEMENT_TYPE_GROUPS.gridElementTypes.includes(element.worksheetElementTypeId);
}

/**
 * Check whether an element type is a text input element (text, textarea, date, time).
 *
 * @param element - the worksheet element to check
 * @returns true if the element accepts text input
 * @example
 * ```ts
 * isInputElementType(textElement); // true
 * ```
 */
export function isInputElementType(element: WorksheetFormElementEntity): boolean {
    return WORKSHEET_ELEMENT_TYPE_GROUPS.inputFormElementTypes.includes(element.worksheetElementTypeId);
}

/**
 * Check whether an element type is a rating-with-text-area element.
 *
 * @param element - the worksheet element to check
 * @returns true if the element is a rating-with-text-area type
 * @example
 * ```ts
 * isRatingWithTextAreaElementType(ratingElement); // true
 * ```
 */
export function isRatingWithTextAreaElementType(element: WorksheetFormElementEntity): boolean {
    return element.worksheetElementTypeId === WORKSHEET_ELEMENT_TYPE_GROUPS.ratingWithTextArea;
}

/**
 * Check whether an element type is read-only (display-only, no answer expected).
 *
 * @param element - the worksheet element to check
 * @returns true if the element is read-only
 * @example
 * ```ts
 * isReadOnlyElementType(headerElement); // true
 * isReadOnlyElementType(inputElement);  // false
 * ```
 */
export function isReadOnlyElementType(element: WorksheetFormElementEntity): boolean {
    return WORKSHEET_ELEMENT_TYPE_GROUPS.readOnlyFieldElementTypes.includes(element.worksheetElementTypeId);
}

/**
 * Validate all answers in a worksheet form. Checks unconditional elements
 * first, then validates conditional elements based on their parent's
 * selected options.
 *
 * This is a pure, synchronous validation function; it does not fetch data.
 *
 * @param worksheetData - the worksheet form entity with current answer state
 * @returns true if all required fields have valid answers
 * @example
 * ```ts
 * const isValid = isValidWorksheetAnswer(worksheetData);
 * ```
 */
export function isValidWorksheetAnswer(worksheetData: WorksheetFormEntity): boolean {
    const elements = worksheetData.elements;
    if (!elements) {
        return true;
    }

    const { readOnlyFieldElementTypes, conditionalOptionFormElementTypes } = WORKSHEET_ELEMENT_TYPE_GROUPS;

    // Collect IDs of elements that are conditional targets
    const conditionalTargetIds = collectConditionalElementIds(elements);

    // Validate all unconditional elements
    const areUnconditionalElementsValid = elements.every((element) => {
        if (!conditionalTargetIds.includes(element.worksheetElementId)) {
            return checkElementAnswer(element);
        }
        return true;
    });

    if (!areUnconditionalElementsValid) {
        return false;
    }

    // Validate conditional elements based on their parent's selected options
    return elements.every((element) => {
        if (
            !readOnlyFieldElementTypes.includes(element.worksheetElementTypeId) &&
            conditionalOptionFormElementTypes.includes(element.worksheetElementTypeId)
        ) {
            if (element.selectedOptions?.length) {
                const selectedOptionIds = element.selectedOptions.map((option) => option.optionId);
                const targetIds = element.options.reduce((ids: string[], option) => {
                    if (selectedOptionIds.includes(option.optionId)) {
                        ids = [...ids, ...option.trgtWorksheetElementIdList];
                    }
                    return ids;
                }, []);

                return elements.every((targetElement) => {
                    if (targetIds.includes(targetElement.worksheetElementId)) {
                        return checkElementAnswer(targetElement);
                    }
                    return true;
                });
            }
            return true;
        }
        return true;
    });
}
